import React, { useMemo } from "react";

import { Form, Formik } from "formik";
import { orderBy } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import { DeploymentAction, GlobalBusinessesAction } from "actions";

import { AdminLink } from "components/AdminLink/AdminLink";
import { Button } from "components/Form";
import { addressValidationSchema } from "components/Form/Fields/AddressForm";
import { BankingDetailsValidationSchema } from "components/Form/Fields/BankingDetailsForm";
import { useSubmitHandler } from "components/Form/FormikControls";
import { sellViaExAgentRebatesValidation } from "components/Form/FormikControls/SellViaExAgentRebates";
import { mapIncomingTerms } from "components/Form/FormikControls/TradingTerms/utils";
import { tradingTermValidation } from "components/Form/FormikControls/TradingTerms/validation";
import { vendorCommissionBandsValidation } from "components/Form/FormikControls/VendorCommissionBands";
import { Column, Row } from "components/Layout";
import { TitleText } from "components/Text";

import { InsuranceApplicationTypes, InsuranceType } from "constants/billing";
import { DeploymentPermission } from "constants/permissions";

import { hasPermission } from "lib/permissions";
import { ABNValidator, validatePercentage } from "lib/validation";

import {
  getActiveLivestockAgentDeployment,
  getInterestSettingsByDeploymentId,
  getVendorCommissionBandsByDeploymentId,
} from "selectors";

import { getDefaultBillingSettings } from "selectors/billingSettings";
import { getDeploymentSellViaExAgentRebatesByBillingSettingsId } from "selectors/sellViaExAgentRebates";
import { getDeploymentTradingTermsByBillingSettingsId } from "selectors/tradingTerms";

import {
  useHasBillingPermission,
  useHasSellViaExAgentRebatesPermission,
  useHasTradingTermsPermission,
  useMountEffect,
  useSectionToggle,
} from "hooks";

import { AddressSection } from "./Address";
import { ContactSection } from "./Contact";
import { EmailSection } from "./Email";
import { FinanceSection } from "./Finance";
import { GeneralSection } from "./General";

const Wrapper = styled.div`
  padding: ${({ theme }) => theme.space[3]}px;
`;

export const FormColumn = styled(Column)(
  ({ theme }) => `
  flex: 1;
  padding: ${theme.space[1]}px;
  &:first-of-type {
    padding-left: 0px;
  }
  &:last-of-type {
    padding-right: 0px;
  }
`,
);

const SubmitRow = styled(Row)(
  ({ theme }) => `
  padding: ${theme.space[2]}px 0 0;
  `,
);

export const DeploymentSettingsModalSection = {
  GENERAL: "GENERAL",
  CONTACT: "CONTACT",
  ADDRESS: "ADDRESS",
  FINANCE_REPORTS: "FINANCE_REPORTS",
  EMAIL: "EMAIL",
};

const MAX_DIGITS = 9;
const DECIMAL_PLACES = 6;

const billingSettingsValidation = Yup.object()
  .nullable()
  .shape({
    abn: ABNValidator,
    emailAddress: Yup.string().nullable().email("Please enter a valid email"),
    bankingDetails: BankingDetailsValidationSchema.nullable(),
    bpayBillerCode: Yup.string().nullable(),
    debtorInsurance: Yup.object()
      .nullable()
      .shape({
        standardRate: validatePercentage(MAX_DIGITS, DECIMAL_PLACES).required(),
      }),
    postalAddress: addressValidationSchema.nullable(),
    transitInsuranceForSales: Yup.object()
      .nullable()
      .shape({
        standardRate: validatePercentage(MAX_DIGITS, DECIMAL_PLACES).required(),
        extendedRate: validatePercentage(MAX_DIGITS, DECIMAL_PLACES),
        providerStandardRate: validatePercentage(MAX_DIGITS, DECIMAL_PLACES),
        providerExtendedRate: validatePercentage(MAX_DIGITS, DECIMAL_PLACES),
      }),
    transitInsuranceForPurchases: Yup.object()
      .nullable()
      .shape({
        standardRate: validatePercentage(MAX_DIGITS, DECIMAL_PLACES).required(),
        extendedRate: validatePercentage(MAX_DIGITS, DECIMAL_PLACES),
        providerStandardRate: validatePercentage(MAX_DIGITS, DECIMAL_PLACES),
        providerExtendedRate: validatePercentage(MAX_DIGITS, DECIMAL_PLACES),
      }),
    paymentPreferences: Yup.array()
      .nullable()
      .of(
        Yup.object().shape({
          bankingDetailId: Yup.string()
            .nullable()
            .required("A bank account is required"),
        }),
      ),
    trackingCategoryPreferences: Yup.array()
      .nullable()
      .of(
        Yup.object().shape({
          xeroTrackingCategoryOptionId: Yup.number()
            .nullable()
            .when("dynamicOption", {
              is: v => !v,
              then: Yup.number()
                .nullable()
                .required(
                  "A tracking option is required when a category is set",
                ),
              otherwise: Yup.number().nullable(),
            }),
        }),
      ),
  });

const deploymentSettingsValidation = Yup.object().nullable().shape({
  physicalAddress: addressValidationSchema.nullable(),
});

const validationSchema = Yup.object().shape({
  corporateColor: Yup.string().required("Corporate colour required."),
  vendorCommissionBands: vendorCommissionBandsValidation,
  tradingTerms: tradingTermValidation,
  sellViaExAgentRebates: sellViaExAgentRebatesValidation,
  billingSettings: billingSettingsValidation,
  deploymentSettings: deploymentSettingsValidation,
});

const billingSettingsNamespace = "billingSettings";
const deploymentSettingsNamespace = "deploymentSettings";

function DeploymentForm() {
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false);
  useSubmitHandler(true, setIsSubmitEnabled);
  const deployment = useSelector(getActiveLivestockAgentDeployment);

  const sectionHelper = useSectionToggle(
    DeploymentSettingsModalSection.GENERAL,
  );

  const hasBillingPermission = useHasBillingPermission();

  return (
    <Form>
      <Column>
        <TitleText>
          Update settings for {deployment.name}
          <AdminLink model="deployment" id={deployment.id} />
        </TitleText>
        <Row>
          <FormColumn>
            <GeneralSection
              sectionHelper={sectionHelper}
              ns={billingSettingsNamespace}
            />
            <ContactSection
              sectionHelper={sectionHelper}
              ns={billingSettingsNamespace}
            />
            <AddressSection
              sectionHelper={sectionHelper}
              ns={billingSettingsNamespace}
              deploymentSettingsNamespace={deploymentSettingsNamespace}
            />
            <FinanceSection
              sectionHelper={sectionHelper}
              ns={billingSettingsNamespace}
            />
            {hasBillingPermission && (
              <EmailSection
                sectionHelper={sectionHelper}
                ns={billingSettingsNamespace}
              />
            )}
          </FormColumn>
        </Row>
        <SubmitRow>
          <Button type="submit" disabled={!isSubmitEnabled} data-tour="Submit">
            Submit
          </Button>
        </SubmitRow>
      </Column>
    </Form>
  );
}

const defaultInsuranceValues = {
  insuranceType: InsuranceType.TRANSIT_INSURANCE,
  standardRate: 0,
  extendedRate: 0,
  providerStandardRate: 0,
  providerExtendedRate: 0,
};

function EditDeployment() {
  const dispatch = useDispatch();

  useMountEffect(() => {
    dispatch(GlobalBusinessesAction.request());
  });

  const deployment = useSelector(getActiveLivestockAgentDeployment);

  // If they have permissions to vendor commissions, pull the data into the form.
  const hasVendorCommissionsFeature = hasPermission(
    deployment,
    DeploymentPermission.featureVendorCommissions,
  );

  const hasTradingTermsFeature = useHasTradingTermsPermission();
  const hasSellViaExAgentRebatesFeature =
    useHasSellViaExAgentRebatesPermission();

  const hasInterestFeature = hasPermission(
    deployment,
    DeploymentPermission.featureInterest,
  );

  const billingSettings = useSelector(getDefaultBillingSettings);

  const initialValues = useMemo(
    () => ({
      defaultDebtorInsurance: deployment.defaultDebtorInsurance,
      corporateColor: deployment.corporateColor,
      billingSettings: billingSettings
        ? {
            ...billingSettings,
            ...(!billingSettings?.generalEmailSettings && {
              generalEmailSettings: { appendNameToSalutation: false },
            }),
            ...(!billingSettings?.rctiEmailSettings && {
              rctiEmailSettings: { appendNameToSalutation: false },
            }),
            ...(!billingSettings?.taxInvoiceEmailSettings && {
              taxInvoiceEmailSettings: { appendNameToSalutation: false },
            }),
            debtorInsurance: billingSettings?.debtorInsurance || {
              insuranceType: InsuranceType.DEBTOR_INSURANCE,
              standardRate: 0,
            },
            transitInsuranceForSales:
              billingSettings?.transitInsuranceForSales ||
              defaultInsuranceValues,
            transitInsuranceForPurchases:
              billingSettings?.transitInsuranceForPurchases ||
              defaultInsuranceValues,
            transitInsuranceApplicationDefault:
              billingSettings?.transitInsuranceApplicationDefault ||
              InsuranceApplicationTypes.N_A,
          }
        : null,
      deploymentSettings: deployment.deploymentSettings,
    }),
    [
      deployment.corporateColor,
      deployment.defaultDebtorInsurance,
      deployment.deploymentSettings,
      billingSettings,
    ],
  );

  const vendorCommissionBands =
    useSelector(getVendorCommissionBandsByDeploymentId(deployment.id)) || [];

  if (hasVendorCommissionsFeature) {
    initialValues.vendorCommissionBands = vendorCommissionBands;
  }

  const interestSettings =
    useSelector(getInterestSettingsByDeploymentId(deployment.id)) || [];

  if (hasInterestFeature) {
    if (interestSettings.length === 0) {
      initialValues.interestSettings = [
        {
          creditInterestRate: 0,
          debitInterestRate: 0,
          daysOverdueThreshold: 0,
          id: uuidv4(),
        },
      ];
    } else {
      initialValues.interestSettings = interestSettings;
    }
  }
  const tradingTerms =
    useSelector(
      getDeploymentTradingTermsByBillingSettingsId(billingSettings?.id),
    ) || [];
  if (hasTradingTermsFeature) {
    initialValues.tradingTerms = orderBy(
      tradingTerms.map(term => mapIncomingTerms(term)),
      ({ saleType, speciesId, saleSubTypeId }) => {
        return `${saleType || ""} ${speciesId || 0} ${saleSubTypeId || 0}`;
      },
    );
  }

  const sellViaExAgentRebates =
    useSelector(
      getDeploymentSellViaExAgentRebatesByBillingSettingsId(
        billingSettings?.id,
      ),
    ) || [];
  if (hasSellViaExAgentRebatesFeature) {
    initialValues.sellViaExAgentRebates = orderBy(
      sellViaExAgentRebates,
      ({ deploymentId, propertyId, saleType, speciesId, saleSubTypeId }) => {
        return `${deploymentId || ""} ${propertyId || ""} ${saleType || ""} ${
          speciesId || 0
        } ${saleSubTypeId || 0}`;
      },
    );
  }

  const onSubmit = values => {
    const billingSettings = values.billingSettings
      ? {
          ...values.billingSettings,
          trackingCategoryPreferences: (
            values.billingSettings.trackingCategoryPreferences ?? []
          ).filter(
            p =>
              p?.xeroTrackingCategoryOptionId != null ||
              p?.dynamicOption != null,
          ),
        }
      : values.billingSettings;
    dispatch(
      DeploymentAction.update({
        id: deployment.id,
        ...values,
        billingSettings,
      }),
    );
  };

  return (
    <Wrapper>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <DeploymentForm />
      </Formik>
    </Wrapper>
  );
}

export default EditDeployment;
